import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  AlertCircle,
  Calendar,
  CheckCircle2,
  ClipboardList,
  Copy,
  Hourglass,
  Link2,
  Loader2,
  LogIn,
  LogOut,
  Pencil,
  Power,
  RefreshCw,
  Trophy,
  User,
  Users,
  X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import entryService, {
  Entry,
  TeamEntry,
  UserRegistration,
} from '../services/entryService';

const ACCENT = '#3fced1';

type TabKey = 'single' | 'teamOwner' | 'teamPlayer';

interface RegisterableInfo {
  id?: string | null;
  name?: string | null;
  slug?: string | null;
  date?: number | null;
}

const MyRegistrationsPage: React.FC = () => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [teamEntriesOwner, setTeamEntriesOwner] = useState<TeamEntry[]>([]);
  const [teamEntriesPlayer, setTeamEntriesPlayer] = useState<TeamEntry[]>([]);
  const [registrations, setRegistrations] = useState<UserRegistration[]>([]);
  const [activeTab, setActiveTab] = useState<TabKey>('single');

  const [toast, setToast] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Abmelden-Dialog
  const [leaveTarget, setLeaveTarget] = useState<{
    kind: 'single' | 'teamOwner' | 'teamPlayer';
    entry: Entry | TeamEntry;
  } | null>(null);

  // Bearbeiten-Dialoge
  const [editEntry, setEditEntry] = useState<Entry | null>(null);
  const [editTeam, setEditTeam] = useState<TeamEntry | null>(null);

  useEffect(() => {
    if (toast) {
      const tid = setTimeout(() => setToast(null), 2500);
      return () => clearTimeout(tid);
    }
  }, [toast]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [singles, owners, playersTeams, regs] = await Promise.all([
        entryService.getUserEntries().catch(() => [] as Entry[]),
        entryService.getTeamEntriesAsOwner().catch(() => [] as TeamEntry[]),
        entryService.getTeamEntriesAsPlayer().catch(() => [] as TeamEntry[]),
        entryService.getUserRegistrations().catch(() => ({ upcoming: [], past: [], total: null })),
      ]);
      setEntries(singles);
      setTeamEntriesOwner(owners);
      // Teams, bei denen man Owner ist, nicht doppelt als Mitspieler anzeigen
      const ownerIds = new Set(owners.map((t) => t.id).filter(Boolean));
      setTeamEntriesPlayer(playersTeams.filter((t) => !t.id || !ownerIds.has(t.id)));
      setRegistrations([...(regs.upcoming || []), ...(regs.past || [])]);
    } catch (err: any) {
      setLoadError(entryService.getErrorMessage(err, 'Anmeldungen konnten nicht geladen werden.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) load();
  }, [isAuthenticated, load]);

  // Turnier-Infos je Entry-Id und je Registerable-Id
  const registerableByEntryId = useMemo(() => {
    const map = new Map<string, RegisterableInfo>();
    for (const reg of registrations) {
      if (reg?.entryId && reg.registerable) map.set(reg.entryId, reg.registerable);
    }
    return map;
  }, [registrations]);

  const registerableById = useMemo(() => {
    const map = new Map<string, RegisterableInfo>();
    for (const reg of registrations) {
      if (reg?.registerable?.id) map.set(reg.registerable.id, reg.registerable);
    }
    return map;
  }, [registrations]);

  const getInfo = useCallback(
    (entry: { id?: string | null; registerable?: string | null }): RegisterableInfo | null => {
      if (entry.id && registerableByEntryId.has(entry.id)) return registerableByEntryId.get(entry.id) || null;
      if (entry.registerable && registerableById.has(entry.registerable)) {
        return registerableById.get(entry.registerable) || null;
      }
      return null;
    },
    [registerableByEntryId, registerableById]
  );

  // ---------------- Aktionen ----------------

  const handleLeaveConfirmed = async () => {
    if (!leaveTarget) return;
    const { kind, entry } = leaveTarget;
    const id = entry.id || '';
    setBusyId(id);
    try {
      if (kind === 'single') {
        if (!entry.registerable) throw new Error('Turnier-Zuordnung fehlt');
        await entryService.leaveRegisterable(entry.registerable);
      } else if (kind === 'teamOwner') {
        if (!entry.registerable) throw new Error('Turnier-Zuordnung fehlt');
        await entryService.leaveRegisterableTeam(entry.registerable);
      } else {
        if (!entry.id) throw new Error('Team-Zuordnung fehlt');
        await entryService.leaveTeamAsPlayer(entry.id);
      }
      setToast('Du wurdest erfolgreich abgemeldet.');
      setLeaveTarget(null);
      await load();
    } catch (err: any) {
      setToast(entryService.getErrorMessage(err, 'Abmeldung fehlgeschlagen.'));
    } finally {
      setBusyId(null);
    }
  };

  const handleCopyInvitation = async (team: TeamEntry) => {
    const link = entryService.buildInvitationLink(team.invitationCode);
    if (!link) {
      setToast('Kein Einladungscode vorhanden.');
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      setToast('Einladungslink kopiert.');
    } catch {
      setToast('Link konnte nicht kopiert werden.');
    }
  };

  const handleRegenerateInvitation = async (team: TeamEntry) => {
    if (!team.id) return;
    setBusyId(team.id);
    try {
      const result = await entryService.regenerateInvitationCode(team.id);
      setTeamEntriesOwner((prev) =>
        prev.map((t) => (t.id === team.id ? { ...t, invitationCode: result?.invitationCode ?? t.invitationCode } : t))
      );
      setToast('Einladungscode erneuert.');
    } catch (err: any) {
      setToast(entryService.getErrorMessage(err, 'Einladungscode konnte nicht erneuert werden.'));
    } finally {
      setBusyId(null);
    }
  };

  const handleToggleInvitation = async (team: TeamEntry) => {
    if (!team.id) return;
    setBusyId(team.id);
    try {
      const result = await entryService.toggleTeamInvitation(team.id);
      setTeamEntriesOwner((prev) =>
        prev.map((t) =>
          t.id === team.id ? { ...t, invitationEnabled: result?.invitationEnabled ?? !(t.invitationEnabled !== false) } : t
        )
      );
      setToast(result?.message || 'Einladungseinstellung geändert.');
    } catch (err: any) {
      setToast(entryService.getErrorMessage(err, 'Einstellung konnte nicht geändert werden.'));
    } finally {
      setBusyId(null);
    }
  };

  // ===================================================================
  // Render
  // ===================================================================

  if (authLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-2 border-white/20 border-t-white animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-black text-white px-4 py-16 flex items-center justify-center">
        <Helmet>
          <title>Meine Anmeldungen | DartCloud</title>
        </Helmet>
        <div className="max-w-md w-full p-6 sm:p-8 rounded-2xl bg-zinc-900 border border-zinc-800 text-center">
          <div className="h-14 w-14 rounded-full mx-auto flex items-center justify-center mb-4" style={{ backgroundColor: `${ACCENT}22` }}>
            <LogIn size={26} style={{ color: ACCENT }} />
          </div>
          <h1 className="text-xl font-bold mb-2">Anmeldung erforderlich</h1>
          <p className="text-sm text-gray-400 mb-6">
            Du musst eingeloggt sein, um deine Turnier-Anmeldungen zu sehen.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center justify-center gap-2 py-3 px-6 rounded-xl font-semibold text-black transition hover:opacity-90"
            style={{ backgroundColor: ACCENT }}
          >
            <LogIn size={16} />
            Jetzt einloggen
          </Link>
        </div>
      </div>
    );
  }

  const tabs: { key: TabKey; label: string; count: number; Icon: React.ComponentType<any> }[] = [
    { key: 'single', label: 'Einzel', count: entries.length, Icon: User },
    { key: 'teamOwner', label: 'Teams (Owner)', count: teamEntriesOwner.length, Icon: Trophy },
    { key: 'teamPlayer', label: 'Teams (Mitspieler)', count: teamEntriesPlayer.length, Icon: Users },
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>Meine Anmeldungen | DartCloud</title>
        <meta name="description" content="Verwalte deine Turnier-Anmeldungen auf DartCloud." />
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="flex items-center gap-3 mb-2">
          <ClipboardList size={28} style={{ color: ACCENT }} />
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Meine Anmeldungen</h1>
        </div>
        <p className="text-gray-400 mb-8">Alle deine Turnier-Anmeldungen auf einen Blick.</p>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {tabs.map((tab) => {
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={
                  'inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-semibold transition ' +
                  (active
                    ? 'border-[#3fced1] bg-[#3fced1]/10 text-[#3fced1]'
                    : 'border-zinc-800 bg-zinc-900 text-gray-300 hover:border-zinc-600')
                }
              >
                <tab.Icon size={16} />
                {tab.label}
                <span
                  className={
                    'px-1.5 py-0.5 rounded-md text-xs ' + (active ? 'bg-[#3fced1]/20' : 'bg-zinc-800')
                  }
                >
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex flex-col items-center py-20">
            <div className="h-10 w-10 rounded-full border-2 border-white/20 border-t-white animate-spin" />
            <p className="mt-4 text-sm text-gray-400">Anmeldungen laden…</p>
          </div>
        ) : loadError ? (
          <div className="p-6 rounded-2xl bg-zinc-900 border border-zinc-800 text-center">
            <AlertCircle size={32} className="mx-auto text-gray-400 mb-3" />
            <p className="text-sm text-gray-400">{loadError}</p>
            <button
              onClick={load}
              className="mt-4 px-4 py-2 rounded-lg bg-white text-black font-medium text-sm hover:bg-gray-200 transition"
            >
              Erneut versuchen
            </button>
          </div>
        ) : (
          <>
            {activeTab === 'single' && (
              <div className="space-y-4">
                {entries.length === 0 ? (
                  <EmptyState text={'Du hast aktuell keine Einzel-Anmeldungen. Melde dich auf einer Turnierseite über „Jetzt anmelden“ an.'} />
                ) : (
                  entries.map((entry) => {
                    const info = getInfo(entry);
                    const status = entryService.getEffectiveStatus(entry);
                    return (
                      <RegistrationCard
                        key={entry.id || `${entry.registerable}-${entry.firstname}`}
                        title={info?.name || 'Turnier'}
                        slug={info?.slug}
                        date={info?.date}
                        status={status}
                        subtitle={`${entry.firstname || ''} ${entry.lastname || ''}`.trim() || undefined}
                        busy={busyId === entry.id}
                        actions={
                          <>
                            <ActionButton Icon={Pencil} label="Bearbeiten" onClick={() => setEditEntry(entry)} />
                            <ActionButton
                              Icon={LogOut}
                              label="Abmelden"
                              danger
                              onClick={() => setLeaveTarget({ kind: 'single', entry })}
                            />
                          </>
                        }
                      />
                    );
                  })
                )}
              </div>
            )}

            {activeTab === 'teamOwner' && (
              <div className="space-y-4">
                {teamEntriesOwner.length === 0 ? (
                  <EmptyState text="Du hast aktuell keine Team-Anmeldungen als Team-Owner." />
                ) : (
                  teamEntriesOwner.map((team) => {
                    const info = getInfo(team);
                    const status = entryService.getEffectiveStatus(team);
                    const invitationLink = entryService.buildInvitationLink(team.invitationCode);
                    const invitationEnabled = team.invitationEnabled !== false;
                    return (
                      <RegistrationCard
                        key={team.id || team.teamName || 'team'}
                        title={info?.name || 'Turnier'}
                        slug={info?.slug}
                        date={info?.date}
                        status={status}
                        subtitle={team.teamName || undefined}
                        players={team.players || []}
                        busy={busyId === team.id}
                        actions={
                          <>
                            <ActionButton Icon={Pencil} label="Bearbeiten" onClick={() => setEditTeam(team)} />
                            <ActionButton
                              Icon={LogOut}
                              label="Team abmelden"
                              danger
                              onClick={() => setLeaveTarget({ kind: 'teamOwner', entry: team })}
                            />
                          </>
                        }
                        footer={
                          <div className="mt-4 pt-4 border-t border-zinc-800">
                            <p className="text-xs font-semibold text-gray-400 mb-2 flex items-center gap-1.5">
                              <Link2 size={14} style={{ color: ACCENT }} />
                              Team-Einladung {invitationEnabled ? '(aktiv)' : '(deaktiviert)'}
                            </p>
                            {invitationLink && invitationEnabled && (
                              <p className="text-xs text-gray-500 break-all mb-3">{invitationLink}</p>
                            )}
                            <div className="flex flex-wrap gap-2">
                              <ActionButton
                                Icon={Copy}
                                label="Link kopieren"
                                small
                                disabled={!invitationLink || !invitationEnabled}
                                onClick={() => handleCopyInvitation(team)}
                              />
                              <ActionButton
                                Icon={RefreshCw}
                                label="Code erneuern"
                                small
                                onClick={() => handleRegenerateInvitation(team)}
                              />
                              <ActionButton
                                Icon={Power}
                                label={invitationEnabled ? 'Einladung deaktivieren' : 'Einladung aktivieren'}
                                small
                                onClick={() => handleToggleInvitation(team)}
                              />
                            </div>
                          </div>
                        }
                      />
                    );
                  })
                )}
              </div>
            )}

            {activeTab === 'teamPlayer' && (
              <div className="space-y-4">
                {teamEntriesPlayer.length === 0 ? (
                  <EmptyState text="Du bist aktuell in keinem Team als Mitspieler angemeldet. Tritt einem Team über einen Einladungslink bei." />
                ) : (
                  teamEntriesPlayer.map((team) => {
                    const info = getInfo(team);
                    const status = entryService.getEffectiveStatus(team);
                    return (
                      <RegistrationCard
                        key={team.id || team.teamName || 'team'}
                        title={info?.name || 'Turnier'}
                        slug={info?.slug}
                        date={info?.date}
                        status={status}
                        subtitle={team.teamName || undefined}
                        players={team.players || []}
                        busy={busyId === team.id}
                        hint="Du bist Mitspieler in diesem Team. Nur der Team-Owner kann die Team-Daten bearbeiten."
                        actions={
                          <ActionButton
                            Icon={LogOut}
                            label="Team verlassen"
                            danger
                            onClick={() => setLeaveTarget({ kind: 'teamPlayer', entry: team })}
                          />
                        }
                      />
                    );
                  })
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Abmelden-Dialog */}
      {leaveTarget && (
        <ConfirmDialog
          title={leaveTarget.kind === 'teamPlayer' ? 'Team verlassen' : 'Abmelden'}
          text={
            leaveTarget.kind === 'teamOwner'
              ? 'Möchtest du dein gesamtes Team wirklich von diesem Turnier abmelden?'
              : leaveTarget.kind === 'teamPlayer'
                ? 'Möchtest du dieses Team wirklich verlassen?'
                : 'Möchtest du dich wirklich von diesem Turnier abmelden?'
          }
          confirmLabel={leaveTarget.kind === 'teamPlayer' ? 'Team verlassen' : 'Abmelden'}
          busy={busyId != null}
          onCancel={() => setLeaveTarget(null)}
          onConfirm={handleLeaveConfirmed}
        />
      )}

      {/* Einzel-Anmeldung bearbeiten */}
      {editEntry && (
        <EditEntryModal
          entry={editEntry}
          onClose={() => setEditEntry(null)}
          onSaved={async () => {
            setEditEntry(null);
            setToast('Deine Änderungen wurden gespeichert.');
            await load();
          }}
        />
      )}

      {/* Team-Anmeldung bearbeiten */}
      {editTeam && (
        <EditTeamModal
          team={editTeam}
          currentUserId={user?.id || null}
          onClose={() => setEditTeam(null)}
          onSaved={async () => {
            setEditTeam(null);
            setToast('Deine Änderungen wurden gespeichert.');
            await load();
          }}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-xl bg-zinc-900 border border-zinc-700 shadow-xl flex items-center gap-2 text-sm max-w-[90vw]">
          <CheckCircle2 size={16} style={{ color: ACCENT }} />
          {toast}
        </div>
      )}
    </div>
  );
};

// =====================================================================
// Sub-Komponenten
// =====================================================================

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const color = entryService.getStatusColor(status);
  const label = entryService.getStatusLabel(status);
  const Icon = status === 'WAITLIST' ? Hourglass : status === 'PAID' || status === 'CONFIRMED' ? CheckCircle2 : Calendar;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border"
      style={{ color, borderColor: `${color}55`, backgroundColor: `${color}15` }}
    >
      <Icon size={12} />
      {label}
    </span>
  );
};

const EmptyState: React.FC<{ text: string }> = ({ text }) => (
  <div className="p-10 rounded-2xl bg-zinc-900 border border-zinc-800 text-center">
    <ClipboardList size={36} className="mx-auto text-gray-600 mb-4" />
    <p className="text-sm text-gray-400 max-w-md mx-auto">{text}</p>
    <Link
      to="/turniere"
      className="inline-block mt-5 px-5 py-2.5 rounded-xl font-semibold text-black text-sm transition hover:opacity-90"
      style={{ backgroundColor: ACCENT }}
    >
      Turniere entdecken
    </Link>
  </div>
);

const ActionButton: React.FC<{
  Icon: React.ComponentType<any>;
  label: string;
  onClick: () => void;
  danger?: boolean;
  small?: boolean;
  disabled?: boolean;
}> = ({ Icon, label, onClick, danger, small, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={
      'inline-flex items-center gap-1.5 rounded-lg border font-semibold transition disabled:opacity-40 ' +
      (small ? 'px-3 py-1.5 text-xs ' : 'px-3.5 py-2 text-sm ') +
      (danger
        ? 'border-red-500/40 text-red-400 hover:bg-red-500/10'
        : 'border-zinc-700 text-gray-200 hover:bg-zinc-800')
    }
  >
    <Icon size={small ? 13 : 15} />
    {label}
  </button>
);

const RegistrationCard: React.FC<{
  title: string;
  slug?: string | null;
  date?: number | null;
  status: string;
  subtitle?: string;
  players?: { firstname?: string | null; lastname?: string | null; isSubstitute?: boolean | null }[];
  hint?: string;
  busy?: boolean;
  actions: React.ReactNode;
  footer?: React.ReactNode;
}> = ({ title, slug, date, status, subtitle, players, hint, busy, actions, footer }) => (
  <div className="p-5 sm:p-6 rounded-2xl bg-zinc-900 border border-zinc-800 relative">
    {busy && (
      <div className="absolute inset-0 rounded-2xl bg-black/50 flex items-center justify-center z-10">
        <Loader2 size={22} className="animate-spin text-white" />
      </div>
    )}
    <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
      <div className="min-w-0">
        {slug ? (
          <Link to={`/turnier/${slug}`} className="text-lg font-bold text-white hover:underline break-words">
            {title}
          </Link>
        ) : (
          <p className="text-lg font-bold text-white break-words">{title}</p>
        )}
        {subtitle && <p className="text-sm text-gray-400 mt-0.5">{subtitle}</p>}
        {typeof date === 'number' && date > 0 && (
          <p className="text-xs text-gray-500 mt-1 inline-flex items-center gap-1">
            <Calendar size={12} />
            {entryService.formatDate(date)}
          </p>
        )}
      </div>
      <StatusBadge status={status} />
    </div>

    {players && players.length > 0 && (
      <div className="flex flex-wrap gap-1.5 mb-3">
        {players.map((p, idx) => (
          <span
            key={idx}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-zinc-800 border border-zinc-700 text-xs text-gray-300"
          >
            <User size={11} className="text-gray-500" />
            {`${p.firstname || ''} ${p.lastname || ''}`.trim() || `Spieler ${idx + 1}`}
            {p.isSubstitute === true && <span className="text-gray-500">(Ersatz)</span>}
          </span>
        ))}
      </div>
    )}

    {hint && <p className="text-xs text-gray-500 mb-3">{hint}</p>}

    <div className="flex flex-wrap gap-2">{actions}</div>
    {footer}
  </div>
);

const ConfirmDialog: React.FC<{
  title: string;
  text: string;
  confirmLabel: string;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ title, text, confirmLabel, busy, onCancel, onConfirm }) => (
  <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4">
    <div className="w-full max-w-md rounded-2xl bg-zinc-900 border border-zinc-800 p-6">
      <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
      <p className="text-sm text-gray-300 mb-6">{text}</p>
      <div className="flex gap-3">
        <button
          onClick={onCancel}
          disabled={busy}
          className="flex-1 py-3 rounded-lg font-semibold border border-zinc-700 text-gray-200 hover:bg-zinc-800 transition disabled:opacity-50"
        >
          Abbrechen
        </button>
        <button
          onClick={onConfirm}
          disabled={busy}
          className="flex-1 py-3 rounded-lg font-semibold text-white bg-red-600 hover:bg-red-500 transition disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {busy && <Loader2 size={16} className="animate-spin" />}
          {confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

const modalInputClass =
  'w-full px-4 py-3 rounded-xl bg-zinc-950 border border-zinc-700 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#3fced1] transition [color-scheme:dark]';

const EditEntryModal: React.FC<{
  entry: Entry;
  onClose: () => void;
  onSaved: () => void;
}> = ({ entry, onClose, onSaved }) => {
  const [firstname, setFirstname] = useState(entry.firstname || '');
  const [lastname, setLastname] = useState(entry.lastname || '');
  const [email, setEmail] = useState(entry.email || '');
  const [phone, setPhone] = useState(entry.phone || '');
  const [birthday, setBirthday] = useState(entryService.birthdayToInputValue(entry.birthday));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!entry.id) return;
    if (!firstname.trim() || !lastname.trim()) {
      setError('Vorname und Nachname sind Pflichtfelder.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await entryService.updateEntry(entry.id, {
        firstname: firstname.trim(),
        lastname: lastname.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        birthday: entryService.inputValueToBirthday(birthday),
        customEntryInputs: entry.customEntryInputs || undefined,
      });
      onSaved();
    } catch (err: any) {
      setError(entryService.getErrorMessage(err, 'Speichern fehlgeschlagen.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4 py-8 overflow-y-auto">
      <div className="w-full max-w-md rounded-2xl bg-zinc-900 border border-zinc-800 p-6 my-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-white">Anmeldung bearbeiten</h3>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-500 hover:text-white hover:bg-zinc-800 transition" aria-label="Schließen">
            <X size={18} />
          </button>
        </div>

        {error && <p className="text-sm text-red-400 mb-4">{error}</p>}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">
              Vorname<span className="text-red-500 ml-1">*</span>
            </label>
            <input value={firstname} onChange={(e) => setFirstname(e.target.value)} className={modalInputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">
              Nachname<span className="text-red-500 ml-1">*</span>
            </label>
            <input value={lastname} onChange={(e) => setLastname(e.target.value)} className={modalInputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">E-Mail</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={modalInputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">Telefon</label>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className={modalInputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">Geburtsdatum</label>
            <input type="date" value={birthday} onChange={(e) => setBirthday(e.target.value)} className={modalInputClass} />
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="mt-6 w-full py-3 rounded-xl font-bold text-black transition hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
          style={{ backgroundColor: ACCENT }}
        >
          {saving && <Loader2 size={16} className="animate-spin" />}
          Änderungen speichern
        </button>
      </div>
    </div>
  );
};

const EditTeamModal: React.FC<{
  team: TeamEntry;
  currentUserId: string | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ team, currentUserId, onClose, onSaved }) => {
  const [teamName, setTeamName] = useState(team.teamName || '');
  const [players, setPlayers] = useState(
    (team.players || []).map((p) => ({
      firstname: p.firstname || '',
      lastname: p.lastname || '',
      email: p.email || null,
      phone: p.phone || null,
      birthday: p.birthday ?? null,
      userId: p.userId || null,
      customEntryInputs: p.customEntryInputs || [],
    }))
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const updatePlayer = (index: number, patch: Partial<{ firstname: string; lastname: string }>) => {
    setPlayers((prev) => prev.map((p, i) => (i === index ? { ...p, ...patch } : p)));
  };

  const handleSave = async () => {
    if (!team.id) return;
    for (let i = 0; i < players.length; i++) {
      if (!players[i].firstname.trim() || !players[i].lastname.trim()) {
        setError(`Vor- und Nachname von Spieler ${i + 1} sind Pflichtfelder.`);
        return;
      }
    }
    setSaving(true);
    setError(null);
    try {
      await entryService.updateTeamEntry(team.id, {
        teamName: teamName.trim() || null,
        customEntryInputs: team.customEntryInputs || [],
        players: players.map((p, i) => ({
          firstname: p.firstname.trim(),
          lastname: p.lastname.trim(),
          email: p.email,
          phone: p.phone,
          birthday: p.birthday,
          // Erster Spieler bleibt mit dem Owner verknüpft (wie Flutter)
          username: i === 0 ? currentUserId : p.userId,
          customEntryInputs: p.customEntryInputs,
        })),
      });
      onSaved();
    } catch (err: any) {
      setError(entryService.getErrorMessage(err, 'Speichern fehlgeschlagen.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4 py-8 overflow-y-auto">
      <div className="w-full max-w-lg rounded-2xl bg-zinc-900 border border-zinc-800 p-6 my-auto max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-white">Team bearbeiten</h3>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-500 hover:text-white hover:bg-zinc-800 transition" aria-label="Schließen">
            <X size={18} />
          </button>
        </div>

        {error && <p className="text-sm text-red-400 mb-4">{error}</p>}

        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">Teamname</label>
            <input value={teamName} onChange={(e) => setTeamName(e.target.value)} className={modalInputClass} />
          </div>

          {players.map((player, index) => (
            <div key={index} className="p-4 rounded-xl bg-zinc-950/60 border border-zinc-800">
              <p className="text-sm font-semibold text-gray-300 mb-3">
                {index === 0 ? 'Spieler 1 (du)' : `Spieler ${index + 1}`}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1">
                    Vorname<span className="text-red-500 ml-0.5">*</span>
                  </label>
                  <input
                    value={player.firstname}
                    onChange={(e) => updatePlayer(index, { firstname: e.target.value })}
                    className={modalInputClass}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1">
                    Nachname<span className="text-red-500 ml-0.5">*</span>
                  </label>
                  <input
                    value={player.lastname}
                    onChange={(e) => updatePlayer(index, { lastname: e.target.value })}
                    className={modalInputClass}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="mt-6 w-full py-3 rounded-xl font-bold text-black transition hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
          style={{ backgroundColor: ACCENT }}
        >
          {saving && <Loader2 size={16} className="animate-spin" />}
          Änderungen speichern
        </button>
      </div>
    </div>
  );
};

export default MyRegistrationsPage;
